/**
 * DEA Audit Emission — Option B: durable insert into the hash-chained audit store.
 *
 * No ingest endpoint, no network POST. Writes cr.dea_access_log via the public
 * security_invoker view; RLS-gated to the OR module. Fire-and-forget; never blocks
 * the save path.
 *
 * The audit table (cr.dea_access_log) is hash-chained by trigger — id, created_at,
 * event_hash, prev_hash, and sync_status are all DB-managed defaults.
 *
 * Patient names are SHA-256 hashed before storing — never plaintext.
 */

import { supabase } from './supabase';

async function sha256Hex(s: string): Promise<string> {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

export interface DeaAuditInput {
    orgId: string;
    userId: string;
    userEmail: string;
    actionType: string; // = transaction_type
    drugName: string;
    ndc?: string | null;
    strength?: string | null; // drug_strength
    schedule?: string | null; // dea_schedule
    quantityDispensed?: number | null; // the moved qty for this txn
    patientId?: number | null;
    patientName?: string | null; // hashed below — never stored plaintext
    prescriberDea?: string | null; // = provider_dea_number
    inventoryBefore?: number | null; // prior running_balance (READ, not computed)
    inventoryAfter?: number | null; // posted row.running_balance (read-back via .select)
    orCaseId?: number | null;
    deaLogId: string; // log_id of the posted dea_controlled_substance_log row
    sessionId?: string | null;
}

/**
 * Emit a DEA audit event. Fire-and-forget — never throws, never blocks the save.
 */
export async function emitDeaAudit(i: DeaAuditInput): Promise<void> {
    try {
        const patient_name_hash = i.patientName
            ? await sha256Hex(i.patientName)
            : null;

        await supabase.from('dea_access_log').insert({
            org_id: i.orgId,
            user_id: i.userId,
            user_email: i.userEmail,
            action_type: i.actionType,
            drug_name: i.drugName,
            ndc: i.ndc ?? null,
            strength: i.strength ?? null,
            schedule: i.schedule ?? null,
            quantity_dispensed: i.quantityDispensed ?? null,
            patient_id: i.patientId ?? null,
            patient_name_hash,
            prescriber_dea: i.prescriberDea ?? null,
            inventory_before: i.inventoryBefore ?? null,
            inventory_after: i.inventoryAfter ?? null,
            or_case_id: i.orCaseId ?? null,
            dea_log_id: i.deaLogId,
            user_agent: navigator.userAgent,
            session_id: i.sessionId ?? null,
            // id, created_at, event_hash, prev_hash, sync_status -> DB defaults / hash trigger
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[deaAudit] non-blocking emission failed:', err);
    }
}
